import React, { useMemo } from 'react';
// Import shared types (adjust path as needed)
// import { Metric } from '../../types/types';

// --- Type Definition (Copied from parent for standalone clarity) ---
export interface Metric {
  category: string;
  item: string;
  value: string | number;
  year: number;
  source?: 'confirmed' | 'manual' | 'ocr';
  readOnly?: boolean;
  tempId?: string; // Optional temporary ID for list keys
}

interface KpiSectionProps {
  items: Metric[]; // Expects items with category 'Occupancy Rate', 'ADR', 'RevPAR'
  years: number[];
  onChange: (updatedItems: Metric[]) => void; // Callback to update parent state
  disabled?: boolean;
}

const KpiSection: React.FC<KpiSectionProps> = ({ items, years, onChange, disabled }) => {
  // Function to find a specific KPI value for a given year
  // Assumes 'item' field might be same as category for KPIs, or needs specific handling
  const findKpiValue = (category: string, year: number): string | number => {
    const item = items.find((i) => i.category === category && i.year === year);
    return item ? item.value : ''; // Return value or empty string
  };

  // Function to find a specific KPI metric object for a given year
  const findKpiMetric = (category: string, year: number): Metric | undefined => {
    return items.find((i) => i.category === category && i.year === year);
  };

  // Handler for when a KPI input value changes
  const handleKpiChange = (category: string, year: number, newValue: string) => {
    let itemExists = false;
    let updatedItems = items.map((item) => {
      if (item.category === category && item.year === year) {
        itemExists = true;
        return { ...item, value: newValue }; // Update value (keep as string for input)
      }
      return item;
    });

    // If the item for that category/year didn't exist, add it
    if (!itemExists) {
      // Determine default 'item' value - using category name as placeholder
      const newItem: Metric = {
        category,
        item: category, // Default item name to category name for KPIs
        year,
        value: newValue,
        tempId: `${category}-${category}-${year}-${Date.now()}`, // Create a unique tempId
      };
      updatedItems.push(newItem);
    }

    // --- Automatic RevPAR Calculation ---
    // If Occupancy or ADR changed, recalculate RevPAR for that year
    if (category === 'Occupancy Rate' || category === 'ADR') {
      const occupancyItem = updatedItems.find((i) => i.category === 'Occupancy Rate' && i.year === year);
      const adrItem = updatedItems.find((i) => i.category === 'ADR' && i.year === year);

      const occupancy = Number(occupancyItem?.value) || 0;
      const adr = Number(adrItem?.value) || 0;

      // Calculate RevPAR (handle potential division by zero or invalid inputs)
      const calculatedRevPar = occupancy > 0 && adr > 0 ? (occupancy / 100) * adr : 0;

      // Find or create the RevPAR item for the year
      let revParExists = false;
      updatedItems = updatedItems.map((item) => {
        if (item.category === 'RevPAR' && item.year === year) {
          revParExists = true;
          // Update existing RevPAR, ensure value is stored appropriately (e.g., rounded)
          return { ...item, value: calculatedRevPar.toFixed(2) };
        }
        return item;
      });

      if (!revParExists) {
        updatedItems.push({
          category: 'RevPAR',
          item: 'RevPAR', // Default item name
          year,
          value: calculatedRevPar.toFixed(2),
          readOnly: true, // Make calculated RevPAR read-only
          tempId: `RevPAR-RevPAR-${year}-${Date.now()}`,
        });
      }
    }

    onChange(updatedItems); // Notify parent component of the change(s)
  };

  return (
    <div className="p-4 border rounded mb-4 shadow-sm">
      {' '}
      {/* Example styling */}
      <h3 className="text-lg font-semibold mb-3">Operational KPIs</h3>
      <div className="space-y-4">
        {years.map((year) => {
          // Find the metric objects for this year to check readOnly status
          const occupancyMetric = findKpiMetric('Occupancy Rate', year);
          const adrMetric = findKpiMetric('ADR', year);
          const revparMetric = findKpiMetric('RevPAR', year);

          return (
            <div key={year} className="p-3 border rounded bg-gray-50">
              {' '}
              {/* Styling for each year block */}
              <h4 className="text-md font-medium mb-2">{year}</h4>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
                {' '}
                {/* Responsive grid */}
                {/* Occupancy Rate */}
                <div>
                  <label htmlFor={`occ-${year}`} className="block text-sm font-medium text-gray-700 mb-1">
                    Occupancy Rate (%)
                  </label>
                  <input
                    id={`occ-${year}`}
                    type="number"
                    value={findKpiValue('Occupancy Rate', year)}
                    onChange={(e) => handleKpiChange('Occupancy Rate', year, e.target.value)}
                    disabled={disabled || occupancyMetric?.readOnly}
                    className={`w-full p-1 border rounded ${occupancyMetric?.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    step="0.1"
                    min="0"
                    max="100"
                  />
                </div>
                {/* ADR */}
                <div>
                  <label htmlFor={`adr-${year}`} className="block text-sm font-medium text-gray-700 mb-1">
                    ADR ($)
                  </label>
                  <input
                    id={`adr-${year}`}
                    type="number"
                    value={findKpiValue('ADR', year)}
                    onChange={(e) => handleKpiChange('ADR', year, e.target.value)}
                    disabled={disabled || adrMetric?.readOnly}
                    className={`w-full p-1 border rounded ${adrMetric?.readOnly ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                    step="0.01"
                    min="0"
                  />
                </div>
                {/* RevPAR */}
                <div>
                  <label htmlFor={`revpar-${year}`} className="block text-sm font-medium text-gray-700 mb-1">
                    RevPAR ($)
                  </label>
                  <input
                    id={`revpar-${year}`}
                    type="number"
                    value={findKpiValue('RevPAR', year)}
                    // RevPAR is calculated, so it's read-only
                    readOnly
                    disabled={disabled} // Still disable if parent is disabled
                    className="w-full p-1 border rounded bg-gray-100 cursor-not-allowed" // Always read-only style
                  />
                </div>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default KpiSection;
